/**
 * Programs dashboard section: list with create/edit slide panel.
 */

import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ProgramsDataTable } from '@/components/programs/ProgramsDataTable';
import { SlidePanel } from '@/components/common/SlidePanel';
import { CreateProgramView } from '@/components/programs/CreateProgramView';
import { EditProgramView } from '@/components/programs/EditProgramView';
import { usePageActions } from '@/hooks/usePageActions';
import type { ElementWidth } from '@/types/interface-elements.types';

/** Props for {@link ProgramsSection}. */
interface ProgramsSectionProps {
  onSuccess?: () => void;
  layoutWidth?: ElementWidth;
}

/** Dashboard widget: programs list. */
export const ProgramsSection: React.FC<ProgramsSectionProps> = ({
  onSuccess,
  layoutWidth = 'full',
}) => {
  const queryClient = useQueryClient();
  const { canCreate, canEditRow, canDeleteRow, canExportCsv } = usePageActions('programs');
  const [createFormOpen, setCreateFormOpen] = React.useState(false);
  const [editProgramId, setEditProgramId] = React.useState<string | null>(null);

  const handleCreateProgram = () => {
    setCreateFormOpen(true);
    setEditProgramId(null);
  };

  const handleEditProgram = (id: string) => {
    setEditProgramId(id);
    setCreateFormOpen(false);
  };

  return (
    <>
      <ProgramsDataTable
        onEditProgram={handleEditProgram}
        layoutWidth={layoutWidth}
        isSection
        showCreateButton={canCreate}
        onCreateProgram={handleCreateProgram}
        pageActions={{ canEditRow, canDeleteRow, canExportCsv }}
      />

      <SlidePanel isOpen={createFormOpen} onClose={() => setCreateFormOpen(false)}>
        <CreateProgramView
          onClose={() => setCreateFormOpen(false)}
          onSuccess={() => {
            setCreateFormOpen(false);
            void queryClient.invalidateQueries({ queryKey: ['programs'] });
            onSuccess?.();
          }}
        />
      </SlidePanel>

      <SlidePanel isOpen={editProgramId !== null} onClose={() => setEditProgramId(null)}>
        {editProgramId && (
          <EditProgramView
            key={editProgramId}
            programId={editProgramId}
            onClose={() => setEditProgramId(null)}
            onSuccess={() => {
              setEditProgramId(null);
              void queryClient.invalidateQueries({ queryKey: ['programs'] });
              onSuccess?.();
            }}
          />
        )}
      </SlidePanel>
    </>
  );
};
